import { inject } from "@angular/core";
import { CanActivateFn, Route, Router, Routes } from "@angular/router";
import { cc7SessionGuard, cc7UserPresentGuard } from "@rinami/cc7-ng";
import { ConfigService } from "./core/config/config.service";
import { questionDetailCanDeactivateGuard } from "./pages/question-detail/question-detail.guard";
import { offlineSyncCanDeactivateGuard } from "./pages/offline-sync/offline-sync.guard";

const authenticatedPageGuards = [cc7UserPresentGuard];

/** Only reachable while the app config failed to load; otherwise (e.g. after a successful reload) redirects home. */
const configurationErrorGuard: CanActivateFn = () => inject(ConfigService).configLoadFailed || inject(Router).createUrlTree(["/"]);

const loadEquipmentSearch = () => import("./pages/equipment-search/equipment-search").then(m => m.EquipmentSearch);
const loadEquipmentHierarchySearch = () =>
  import("./pages/equipment-hierarchy-search/equipment-hierarchy-search").then(m => m.EquipmentHierarchySearch);
const loadEquipmentMapSearch = () => import("./pages/equipment-map-search/equipment-map-search").then(m => m.EquipmentMapSearch);
const loadEquipmentScanner = () => import("./pages/equipment-scanner/equipment-scanner").then(m => m.EquipmentScanner);
const loadDataLoading = () => import("./pages/data-loading/data-loading").then(m => m.DataLoading);
const loadWorkRequest = () => import("./pages/work-request/work-request").then(m => m.WorkRequest);
const loadOfflineSync = () => import("./pages/offline-sync/offline-sync").then(m => m.OfflineSync);
const loadChecklist = () => import("./pages/checklist/checklist").then(m => m.Checklist);
const loadChecklistCompletion = () => import("./pages/checklist-completion/checklist-completion").then(m => m.ChecklistCompletion);
const loadQuestionDetail = () => import("./pages/question-detail/question-detail").then(m => m.QuestionDetail);
const loadAttachmentsList = () => import("./pages/attachments-list/attachments-list").then(m => m.AttachmentsList);
const loadAuditInfo = () => import("./pages/audit-info/audit-info").then(m => m.AuditInfo);
const loadAppLogs = () => import("./pages/app-logs/app-logs").then(m => m.AppLogs);
const loadLanguageSelection = () => import("./pages/language-selection/language-selection").then(m => m.LanguageSelection);
const loadPhotoMarkup = () => import("./shared-ui/photo-markup/photo-markup").then(m => m.PhotoMarkup);
const loadMediaPreview = () => import("./shared-ui/media-preview/media-preview").then(m => m.MediaPreview);

const photoChildRoutes: Routes = [
  {
    path: "photo-markup",
    canActivate: authenticatedPageGuards,
    loadComponent: loadPhotoMarkup,
  },
  {
    path: "media-preview",
    canActivate: authenticatedPageGuards,
    loadComponent: loadMediaPreview,
  },
];

const buildEquipmentPickerRoutes = (includeHierarchy: boolean): Routes => {
  const routes: Route[] = [];

  if (includeHierarchy) {
    routes.push({
      path: "equipment-hierarchy-search",
      canActivate: authenticatedPageGuards,
      loadComponent: loadEquipmentHierarchySearch,
      children: buildEquipmentPickerRoutes(false),
    });
  }

  routes.push(
    {
      path: "equipment-search",
      canActivate: authenticatedPageGuards,
      loadComponent: loadEquipmentSearch,
    },
    {
      path: "equipment-map-search",
      canActivate: authenticatedPageGuards,
      loadComponent: loadEquipmentMapSearch,
    },
    {
      path: "equipment-scanner",
      canActivate: authenticatedPageGuards,
      loadComponent: loadEquipmentScanner,
    }
  );

  return routes;
};

const workRequestRoute: Route = {
  path: "work-request",
  canActivate: authenticatedPageGuards,
  loadComponent: loadWorkRequest,
  children: [...buildEquipmentPickerRoutes(true), ...photoChildRoutes],
};

const questionDetailRoute: Route = {
  path: "question-detail/:questionIndex",
  canActivate: authenticatedPageGuards,
  canDeactivate: [questionDetailCanDeactivateGuard],
  loadComponent: loadQuestionDetail,
  children: [
    {
      path: "attachments-list",
      canActivate: authenticatedPageGuards,
      loadComponent: loadAttachmentsList,
    },
    ...photoChildRoutes,
  ],
};

const checklistRoute: Route = {
  path: "checklist/:workOrderNumber",
  canActivate: authenticatedPageGuards,
  loadComponent: loadChecklist,
  children: [
    questionDetailRoute,
    {
      path: "checklist-completion",
      canActivate: authenticatedPageGuards,
      loadComponent: loadChecklistCompletion,
    },
    workRequestRoute,
  ],
};

export const routes: Routes = [
  {
    path: "",
    canActivate: [cc7SessionGuard],
    loadComponent: () => import("./pages/home/home").then(m => m.Home),
  },
  {
    path: "security",
    children: [
      {
        path: "",
        pathMatch: "full",
        redirectTo: "login",
      },
      {
        path: "login",
        loadComponent: () => import("./pages/login/login-page").then(m => m.LoginPage),
      },
    ],
  },
  {
    path: "equipment-search",
    canActivate: authenticatedPageGuards,
    loadComponent: loadEquipmentSearch,
  },
  {
    path: "equipment-hierarchy-search",
    canActivate: authenticatedPageGuards,
    loadComponent: loadEquipmentHierarchySearch,
    children: buildEquipmentPickerRoutes(false),
  },
  {
    path: "equipment-map-search",
    canActivate: authenticatedPageGuards,
    loadComponent: loadEquipmentMapSearch,
  },
  {
    path: "equipment-scanner",
    canActivate: authenticatedPageGuards,
    loadComponent: loadEquipmentScanner,
  },
  {
    path: "data-loading",
    canActivate: authenticatedPageGuards,
    loadComponent: loadDataLoading,
  },
  workRequestRoute,
  {
    path: "audit-info",
    canActivate: authenticatedPageGuards,
    loadComponent: loadAuditInfo,
  },
  {
    path: "app-logs",
    canActivate: authenticatedPageGuards,
    loadComponent: loadAppLogs,
  },
  {
    path: "offline-sync",
    canActivate: authenticatedPageGuards,
    canDeactivate: [offlineSyncCanDeactivateGuard],
    loadComponent: loadOfflineSync,
  },
  {
    path: "language-selection",
    canActivate: authenticatedPageGuards,
    loadComponent: loadLanguageSelection,
  },
  {
    path: "media-preview",
    canActivate: authenticatedPageGuards,
    loadComponent: loadMediaPreview,
  },
  {
    path: "photo-markup",
    canActivate: authenticatedPageGuards,
    loadComponent: loadPhotoMarkup,
  },
  checklistRoute,
  questionDetailRoute,
  {
    path: "attachments-list",
    canActivate: authenticatedPageGuards,
    loadComponent: loadAttachmentsList,
  },
  {
    path: "checklist-completion",
    canActivate: authenticatedPageGuards,
    loadComponent: loadChecklistCompletion,
  },
  {
    path: "configuration-error",
    canActivate: [configurationErrorGuard],
    loadComponent: () => import("./pages/configuration-error/configuration-error").then(m => m.ConfigurationError),
  },
  {
    path: "sso-callback",
    loadComponent: () => import("@rinami/cc7-ng").then(m => m.Cc7SsoCallbackComponent),
  },
  {
    path: "inspection-list",
    canActivate: authenticatedPageGuards,
    loadComponent: () => import("./pages/inspection-list/inspection-list").then(m => m.InspectionList),
    children: [
      checklistRoute,
      workRequestRoute,
      {
        path: "data-loading",
        canActivate: authenticatedPageGuards,
        loadComponent: loadDataLoading,
      },
      {
        path: "audit-info",
        canActivate: authenticatedPageGuards,
        loadComponent: loadAuditInfo,
      },
      {
        path: "app-logs",
        canActivate: authenticatedPageGuards,
        loadComponent: loadAppLogs,
      },
      {
        path: "offline-sync",
        canActivate: authenticatedPageGuards,
        canDeactivate: [offlineSyncCanDeactivateGuard],
        loadComponent: loadOfflineSync,
      },
      {
        path: "language-selection",
        canActivate: authenticatedPageGuards,
        loadComponent: loadLanguageSelection,
      },
      ...buildEquipmentPickerRoutes(true),
    ],
  },
  {
    path: "**",
    redirectTo: "",
  },
];
